import { CreateCustomBuydownPlanDto, CustomBuydownPlan } from '@src/types/CustomBuydown'
import { QueryFunctionContext, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { getApiClient } from '../api-client'

const SCOPE = 'custom-buydown'
const DETAIL = 'detail'

const keysFactory = {
  all: () => [{ scope: SCOPE }] as const,
  detail: (merchantId: string) => [{ scope: SCOPE, entity: DETAIL, merchantId }] as const,
}

const getCurrentCustomBuydownPlans = async ({
  queryKey: [{ merchantId }],
}: QueryFunctionContext<ReturnType<(typeof keysFactory)['detail']>>) => {
  const apiClient = getApiClient()
  const response = await apiClient.get<CustomBuydownPlan[]>(
    `/Merchant/${encodeURIComponent(merchantId)}/CustomBuydown`,
  )
  return response.data
}

export function useCurrentCustomBuydownPlan(merchantId: string): [CustomBuydownPlan | null, boolean] {
  const { isFetching, data } = useQuery({
    queryKey: keysFactory.detail(merchantId),
    queryFn: getCurrentCustomBuydownPlans,
    enabled: !!merchantId,
  })

  return [data?.[0] ?? null, isFetching]
}

export interface SaveCustomBuydownPlanParams {
  planKey?: string
  dto: CreateCustomBuydownPlanDto
}

export function useSaveCustomBuydownPlan(merchantId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ planKey, dto }: SaveCustomBuydownPlanParams) => {
      const apiClient = getApiClient()
      const url = `/Merchant/${encodeURIComponent(merchantId)}/CustomBuydown`
      const response = planKey
        ? await apiClient.put<CustomBuydownPlan>(`${url}/${encodeURIComponent(planKey)}`, dto)
        : await apiClient.post<CustomBuydownPlan>(url, dto)
      return response.data
    },
    onSuccess: async (saved) => {
      queryClient.setQueryData(keysFactory.detail(merchantId), [saved])
      await queryClient.invalidateQueries({ queryKey: [{ scope: 'merchants', entity: 'detail', id: merchantId }] })
    },
  })
}
